import { Location, PopStateEvent } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthenticationService, OperationsAlertsService } from '@uzurv/clara';
import { BugsnagService, isNullOrUndefined } from '@uzurv/core';
import { AuthDataService } from '@uzurv/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AlertDialogComponent } from '../components/dialogs/alert-dialog/alert-dialog.component';
import { UserIdleService } from '../services/idle/idle.service';
import { APP_CONFIGURATION, AppConfiguration } from './app-configuration';
import { Logout } from './store/clear-state.actions';
import { State } from './store/clear-state.reducer';

@Injectable()
export class AppSetupService implements OnDestroy {
  private timeoutModalVisible = false;
  private idleTimeoutMinutes = this.appConfiguration.authTimeoutMinutes;
  private lastPoppedUrl: string;
  private yScrollStack: Array<number> = [];

  private destroy$ = new Subject<boolean>();

  constructor(
    private title: Title,
    private authenticationService: AuthenticationService,
    private authDataService: AuthDataService,
    private dialog: MatDialog,
    private userIdleService: UserIdleService,
    private router: Router,
    private location: Location,
    private zone: NgZone,
    private store$: Store<State>,
    private bugsnagService: BugsnagService,
    private operationAlertsService: OperationsAlertsService,
    @Inject(APP_CONFIGURATION) private appConfiguration: AppConfiguration
  ) {
    this.authDataService.loginEvents.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.userIdleService.startWatching();
    });

    this.authDataService.logoutEvents.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.store$.dispatch(new Logout());
      this.userIdleService.stopWatching();

      this.zone.run(() => {
        this.router.navigate(['/login']); // this.unAssignOperationsAlerts();
      });
    });

    // Check against last activity timestamp
    if (!isNullOrUndefined(localStorage.getItem('lastActivityTimestamp'))) {
      // convert to miliseconds
      const lastActivityDate: Date = new Date(+localStorage.getItem('lastActivityTimestamp') * 1000);
      const now: Date = new Date();
      const minutesDiff = (now.getTime() - lastActivityDate.getTime()) / 60000;
      if (minutesDiff > this.idleTimeoutMinutes) {
        this.bugsnagService.leaveBreadcrumb('Calling authenticationService.logout based on lastActivityTimestamp');
        this.authenticationService.logout();
        localStorage.removeItem('lastActivityTimestamp');
      }
    }

    // Start watching when user idle is starting.
    this.userIdleService.setConfigValues({
      timeout: this.idleTimeoutMinutes * 60,
      timeoutWarningSeconds: 5 * 60
    });

    this.userIdleService
      .onTimeoutWarning()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.timeoutModalVisible) {
          this.timeoutModalVisible = true;
          this.dialog
            .open(AlertDialogComponent, {
              data: {
                title: 'Still There?',
                message: 'You are about to be logged out for inactivity.  Would you like to stay logged in?'
              }
            })
            .afterClosed()
            .subscribe(() => {
              this.timeoutModalVisible = false;
            });
        }
      });

    // When time is up.
    this.userIdleService
      .onTimeout()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.authDataService.hasPermission({ any: ['operations_alerts_update'] })
          ? this.operationAlertsService.unassignAllByUserId(this.authDataService.currentUser.userId).subscribe()
          : null;
        this.bugsnagService.leaveBreadcrumb('Calling authenticationService.logout based on idleTimeout');
        this.authenticationService.logout();
        this.dialog.closeAll();
        this.timeoutModalVisible = true;

        this.dialog
          .open(AlertDialogComponent, {
            data: {
              title: 'Session Timeout',
              message: 'You have been logged out due to inactivity'
            }
          })
          .afterClosed()
          .subscribe(() => {
            this.timeoutModalVisible = false;
          });
        this.userIdleService.stopWatching();
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  setupApp() {
    this.title.setTitle('UZURV CRM');

    if (this.authDataService.isLoggedIn) {
      this.authDataService.sendUserLoggedInEvent();
    }

    this.location.subscribe((ev: PopStateEvent) => {
      this.lastPoppedUrl = ev.url;
    });
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((ev: any) => {
      if (ev instanceof NavigationStart) {
        if (ev.url !== this.lastPoppedUrl) {
          this.yScrollStack.push(window.scrollY);
        }
      } else if (ev instanceof NavigationEnd) {
        if (ev.url === this.lastPoppedUrl) {
          this.lastPoppedUrl = undefined;
          window.scrollTo(0, this.yScrollStack.pop());
        } else {
          window.scrollTo(0, 0);
        }
      }
    });
  }
}
